import "core-js/features/bigint";
import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import FloatingMessage from "./utilities/FloatingMessage";
import { Link } from "react-router-dom";
import useResponsiveFlags from "../hooks/useResponsiveFlags";
import WrapperContainer from "./utilities/WrapperContainer";
import axios from "../api/axios";
import { useAuth } from "../api/authUtils/AuthContext";

function InfoTooltip() {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const iconRef = useRef(null);
  const wrapperRef = useRef(null);

  const updateCoords = () => {
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      setCoords({
        top:  rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX + rect.width / 2,
      });
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setVisible(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const tooltip = visible
    ? ReactDOM.createPortal(
        <div
          role="tooltip"
          style={{
            position: "absolute",
            top: coords.top,
            left: coords.left,
            transform: "translateX(-50%)",
            width: "210px",
            background: "#333",
            color: "#fff",
            fontSize: "12px",
            lineHeight: "1.5",
            padding: "8px 10px",
            borderRadius: "6px",
            zIndex: 99999,
            pointerEvents: "none",
            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
          }}
        >
          Once you submit a request, our team will review it and begin the
          access provisioning process shortly.
          <span
            style={{
              position: "absolute",
              bottom: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderBottom: "6px solid #333",
            }}
          />
        </div>,
        document.body
      )
    : null;

  return (
    <span
      ref={wrapperRef}
      style={{ position: "relative", display: "inline-flex", alignItems: "center" }}
      onMouseEnter={() => { updateCoords(); setVisible(true); }}
      onMouseLeave={() => setVisible(false)}
    >
      <button
        ref={iconRef}
        onClick={() => { updateCoords(); setVisible((v) => !v); }}
        aria-label="More information about access request"
        style={{
          background: "none", border: "none", padding: "0 2px",
          cursor: "pointer", display: "inline-flex", alignItems: "center",
          color: "#6c757d", verticalAlign: "middle",
        }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15"
          viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="8.5" />
          <polyline points="11 11 12 11 12 16" />
        </svg>
      </button>
      {tooltip}
    </span>
  );
}

function RequestAccessButton({ moduleType, onSuccess, onError, initialRequested }) {
  const [requested, setRequested] = useState(initialRequested);
  const [loading, setLoading]     = useState(false);

  useEffect(() => {
    setRequested(initialRequested);
  }, [initialRequested]);

  const handleRequest = async () => {
    if (requested || loading) return;
    setLoading(true);
    try {
      await axios.post("/api/request-access", { moduleType });
      setRequested(true);
      onSuccess(`Access request for iSort™ ${moduleType} submitted successfully.`);
    } catch (err) {
      onError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to submit access request. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleRequest}
      disabled={requested || loading}
      className="run-locally-card-link storage-limit"
      style={{
        background: "none", border: "none", padding: 0,
        cursor: requested || loading ? "not-allowed" : "pointer",
        opacity: requested || loading ? 0.55 : 1,
        fontSize: "inherit", fontFamily: "inherit", verticalAlign: "middle",
      }}
    >
      {loading ? "Requesting…" : requested ? "Request Sent ✓" : "Request Access"}
    </button>
  );
}

function NoAccessRow({ moduleType, onSuccess, onError, initialRequested }) {
  return (
    <p className="review-text storage-limit"
      style={{ marginBottom: "0", display: "flex", alignItems: "center", flexWrap: "wrap", gap: "6px" }}
    >
      You do not have access to download the module.
      <RequestAccessButton
        moduleType={moduleType}
        onSuccess={onSuccess}
        onError={onError}
        initialRequested={initialRequested}
      />
      <InfoTooltip />
    </p>
  );
}

export function RunLocallyCard(props) {
  return (
    <>
      <div className="col-12 col-lg-6">
        <div className="card run-locally-cards">
          <div className="card-body d-flex flex-column justify-content-between">
            <div>
              <p className="custom-title form-label">{props.title}</p>
              <p className="card-text">
                <span className="review-text storage-limit">
                  {props.storagelimit}
                </span>
                <br />
                {props.text}
              </p>
            </div>
            <div style={{ marginTop: "1rem" }}>{props.link}</div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function RunLocally() {
  const { isDesktop } = useResponsiveFlags();
  const [desktopDevice, setDesktopDevice] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const { user } = useAuth();
  useEffect(() => {
    setDesktopDevice(
      !/Mobi|Android|iPhone|iPad|Tablet/i.test(navigator.userAgent)
    );
  }, [isDesktop]);

  const handleDownload = async (filename, filetype) => {
    axios
      .post("/api/download-modules", {
        filetype: filetype,
        filename: filename
      })
      .then((res) => {
        setMessage("Downloading started successfully");
        const downloadUrl = res.data.downloadUrl;
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch((err) => {
        setError(
          err.response?.data?.message
            ? err.response.data.message
            : err.response?.data?.error
            ? err.response.data.error
            : `Failed to download the requested file`
        );
      });
  };

  const mobileWarning = (
    <p className="review-text storage-limit" style={{ marginBottom: "0" }}>
      Please use a desktop machine to download
    </p>
  );

  return (
    <>
      {message && (
        <FloatingMessage
          message={message}
          duration={20000}
          onClose={() => setMessage(null)}
          top={isDesktop ? "20px" : ""}
        />
      )}
      {error && (
        <FloatingMessage
          message={error}
          duration={20000}
          onClose={() => setError(null)}
          isError={true}
          top={isDesktop ? "20px" : ""}
        />
      )}
      <WrapperContainer
        maxwidth="70%"
        loggedin={true}
        title="Run iSort&trade; Locally"
        content={
          <>
            <div>
              <p className="custom-title">
                <strong>Introduction</strong>
              </p>
              <p className="custom-size para-dark-grey">
                The <strong>stand-alone version of iSort&trade;</strong> is a
                command-line tool that runs locally on user-managed systems,
                enabling analysis without uploading data to external servers.
              </p>
            </div>
            <div style={{ marginTop: "20px" }}>
              <h6 className="sub-step-link">System Requirements</h6>
              <p className="custom-size para-dark-grey">
                iSort&trade; modules are containerized by Docker. <br />
                <strong>iSort&trade; Fractions</strong> and{" "}
                <strong>iSort&trade; HiRes</strong> can be run on any OS with
                Docker installed.
              </p>
            </div>
            <div>
              <h6 className="sub-step-link">To Run iSort&trade; Locally</h6>
              <p className="custom-size para-dark-grey">
                A valid{" "}
                <Link to="/tokens" className="run-locally-card-link">
                  token
                </Link>{" "}
                is required to run iSort&trade; locally.
                <br />
                Download User Guide and iSort&trade; Package of interest.
                <br />
                Follow the User Guide for installation, preparation of input
                file(s), and run commands.
              </p>
            </div>
            <div style={{ marginTop: "40px" }}>
              <p className="custom-title">Downloads</p>
              <div
                className="row card-row-1"
                // style={{ padding: !isMobile ? "0 0 12px 0" : "" }}
              >
                <RunLocallyCard
                  title="iSort&trade; Fractions User Guide"
                  link={
                    <a
                      href="iSortFractions_UserGuide_latest.pdf"
                      target="__blank"
                      rel="noreferrer"
                      className="run-locally-card-link storage-limit"
                    >
                      Download
                    </a>
                  }
                />
                <RunLocallyCard
                  title="iSort&trade; HiRes User Guide"
                  link={
                    <a
                      href="iSortHiRes_UserGuide_latest.pdf"
                      target="__blank"
                      rel="noreferrer"
                      className="run-locally-card-link storage-limit"
                    >
                      Download
                    </a>
                  }
                />
              </div>
              <div
                className="row card-row-2"
                // style={{ padding: !isMobile ? "12px 0" : "" }}
              >
                <RunLocallyCard
                  title="iSort&trade; Fractions Package"
                  storagelimit="589MB"
                  text="The iSort&trade; Fractions package. Contains the iSort&trade; Fractions Docker image, a demo input file and the expected output files from the demo data set."
                  link={
                    user?.frac_access ? (
                      !desktopDevice ? mobileWarning : (
                        <a
                          href="#"
                          onClick={() =>
                            handleDownload(
                              "iSort_Fractions_PACKAGE_latest.tar.gz",
                              "Fractions"
                            )
                          }
                          className="run-locally-card-link storage-limit"
                        >Download</a>
                      )
                    ) : (
                      <NoAccessRow
                        moduleType="Fractions"
                        onSuccess={(msg) => setMessage(msg)}
                        onError={(err) => setError(err)}
                        initialRequested={user?.frac_requested}
                      />
                    )
                  }
                />
                <RunLocallyCard
                  title="iSort&trade; HiRes Package"
                  storagelimit="948 MB"
                  text="The iSort&trade; HiRes package. Contains the iSort&trade; HiRes Docker image and demo input files."
                  link={
                    user?.hires_access ? (
                      !desktopDevice ? mobileWarning : (
                        <a
                          href="#"
                          onClick={() =>
                            handleDownload(
                              "iSort_HiRes_PACKAGE_latest.tar.gz",
                              "HiRes"
                            )
                          }
                          className="run-locally-card-link storage-limit"
                        >Download</a>
                      )
                    ) : (
                      <NoAccessRow
                        moduleType="HiRes"
                        onSuccess={(msg) => setMessage(msg)}
                        onError={(err) => setError(err)}
                        initialRequested={user?.hires_requested}
                      />
                    )
                  }
                />
              </div>
              <div
                className="row card-row-2"
                // style={{ padding: !isMobile ? "12px 0" : "" }}
              >
                <RunLocallyCard
                  title="Salmon Index Files (Optional)"
                  storagelimit="15 GB"
                  text="Index files necessary for Salmon 1.9.0 to run. Using
                        Salmon is optional, and download of these index files is
                        only necessary if the user plans to use the Salmon
                        module of the iSort&trade; package to map and quantify
                        reads."
                  link={
                    !desktopDevice ? mobileWarning : (
                      <a
                        href="#"
                        onClick={() => handleDownload("salmon-1.9.0.indexes.tar.gz","salmon")}
                        className="run-locally-card-link storage-limit"
                      >Download</a>
                    )
                  }
                />
              </div>
            </div>
          </>
        }
      />
    </>
  );
}